
import { computed, defineComponent, PropType, Ref, ref } from 'vue'
import { ErrorObject } from '@vuelidate/core'

import NewInput from '@/ui-kit/NewInput/index.vue'
import NewCalendar from '@/ui-kit/NewCalendar/index.vue'

type NewCalendarFormat = 'iso' | 'readable' | 'date'
type NewCalendarValue = Date | string | Array<string> | Array<Date>
type NewCalendarDisabledDates = 'before-today' | 'after-today' | Array<string> | 'week-limit'

import { useOnOutsidePress } from 'vue-composable'

export default defineComponent({
    name: 'new-date-picker',
    components: {
        NewInput,
        NewCalendar,
    },
    props: {
        isInvalid: {
            type: Boolean,
            default: false,
        },
        validationErrors: {
            type: Array as PropType<ErrorObject[]>,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: null,
        },
        disabledDates: {
            type: [Array, String] as PropType<NewCalendarDisabledDates>,
            default: null,
        },
        format: {
            type: String as PropType<NewCalendarFormat>,
            default: 'iso',
        },
        value: {
            type: [Date, String, Array] as PropType<NewCalendarValue>,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        range: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value', 'change'],
    setup(props, { emit }) {
        const is_opened = ref(false)
        const elementref: Ref<HTMLElement | null> = ref(null)

        const onChange = (value: NewCalendarValue) => {
            if (props.disabled) return
            emit('update:value', value)
            emit('change', value)
        }

        const formatIsoToReadable = (isoDate: string) => {
            return isoDate.split('-').reverse().join('.')
        }

        const formatDateToReadable = (date: Date) => {
            const year = date.getFullYear()
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const day = date.getDate().toString().padStart(2, '0')
            return `${day}.${month}.${year}`
        }

        const onClick = () => {
            is_opened.value = !is_opened.value
        }

        const closeCalendar = () => {
            is_opened.value = false
        }

        useOnOutsidePress(elementref, closeCalendar)

        const computedValue = computed(() => {
            if (props.value) {
                if (props.range && Array.isArray(props.value)) {
                    let valueDateFrom = null as string | null
                    let valueDateTo = null as string | null

                    if (props.value.length === 0) {
                        return null
                    }

                    if (props.format === 'iso') {
                        valueDateFrom = props.value[0]
                            ? formatIsoToReadable(props.value[0] as string)
                            : '*'
                        valueDateTo = props.value[1]
                            ? formatIsoToReadable(props.value[1] as string)
                            : '*'
                    }

                    if (props.format === 'readable') {
                        valueDateFrom = props.value[0] ? (props.value[0] as string) : '*'
                        valueDateTo = props.value[1] ? (props.value[1] as string) : '*'
                    }

                    if (props.format === 'date') {
                        valueDateFrom = props.value[0]
                            ? formatDateToReadable(props.value[0] as Date)
                            : '*'
                        valueDateTo = props.value[1]
                            ? formatDateToReadable(props.value[1] as Date)
                            : '*'
                    }

                    return `${valueDateFrom} - ${valueDateTo}`
                }

                if (props.format === 'iso') {
                    return formatIsoToReadable(props.value as string)
                }

                if (props.format === 'readable') {
                    return props.value as string
                }

                if (props.format === 'date') {
                    return formatDateToReadable(props.value as Date)
                }
            }

            return null
        })

        return {
            elementref,
            is_opened,
            computedValue,
            onClick,
            onChange,
        }
    },
})
